import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Store as ngrxStore } from '@ngrx/store';
import { Organization } from 'app/models/organization.model';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatMenuTrigger } from '@angular/material/menu';
import { setLanguage, signOutUser } from 'app/reducers/context.actions';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationUser } from 'app/models/user.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RoleMembership, selectHasPermission, selectLoginData, selectRole, selectTokenInfo } from "app/reducers/context.selectors";
import {environment} from "environments/environment";
import {
  ContentManagerPermissionCode,
  ControlCenterPermissionCode,
  GetPosPermissionCode,
  KioskPermissionCode,
  PermissionCode,
  QrMenuPermissionCode,
} from 'app/helpers/permission-codes';

type PublishedFor = 'staging' | 'kiosk' | 'production';
@UntilDestroy()
@Component({
  selector: 'app-internal',
  templateUrl: './internal.component.html',
  styleUrls: ['./internal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('openClose', [
      // ...
      state('open', style({
        width: '240px'
      })),
      state('closed', style({
        width: '72px'
      })),
      state('openpanel', style({
        width: 'calc(100%-240px)'
      })),
      state('closedpanel', style({
        width: 'calc(100% - 72px)'
      })),
      transition('open => closed', [
        animate('0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)')
      ]),
      transition('closed => open', [
        animate('0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)')
      ]),
      transition('openpanel => closedpanel', [
        animate('0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)')
      ]),
      transition('closedpanel => openpanel', [
        animate('0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)')
      ]),
    ]),
  ]
})
export class InternalComponent implements OnInit {

  user: ApplicationUser | null = null;


  selectedOrganizationId: string;

  isOpen = true;
  public roleMembership: RoleMembership;

  // Видимость разделов меню по правам просмотра (сисадмин получает true на всё).
  public hasOrganizationView$ = this.ngrxStore.select(selectHasPermission(PermissionCode.OrganizationView));
  public hasUserView$ = this.ngrxStore.select(selectHasPermission(PermissionCode.UserView));
  public hasRoleView$ = this.ngrxStore.select(selectHasPermission(PermissionCode.RoleView));
  public hasGetPosMenuView$ = this.ngrxStore.select(selectHasPermission(GetPosPermissionCode.GetPosMenuView));
  public hasGetPosProductView$ = this.ngrxStore.select(selectHasPermission(GetPosPermissionCode.GetPosProductView));
  public hasGetPosPromoView$ = this.ngrxStore.select(selectHasPermission(GetPosPermissionCode.GetPosPromoView));
  public hasOrderView$ = this.ngrxStore.select(selectHasPermission(PermissionCode.OrderView));
  public hasGetPosOrganizationView$ = this.ngrxStore.select(selectHasPermission(GetPosPermissionCode.GetPosOrganizationView));
  public hasPipeView$ = this.ngrxStore.select(selectHasPermission(PermissionCode.PipeView));
  public hasConfigurationView$ = this.ngrxStore.select(selectHasPermission(PermissionCode.ConfigurationView));
  public hasReportView$ = this.ngrxStore.select(selectHasPermission(PermissionCode.ReportView));

  // Ссылки на микрофронты видны по кодам `*View` их блоков (приходят в токене вместе с хабовыми),
  // тонкие права внутри разделов проверяют сами дочерние SPA и их бэкенды.
  public hasKioskSettingsView$ = this.ngrxStore.select(selectHasPermission(KioskPermissionCode.KioskSettingsView));
  public hasControlCenterView$ = this.ngrxStore.select(selectHasPermission(ControlCenterPermissionCode.ControlCenterView));
  public hasContentManagerView$ = this.ngrxStore.select(selectHasPermission(ContentManagerPermissionCode.ContentManagerView));
  public hasQrMenuView$ = this.ngrxStore.select(selectHasPermission(QrMenuPermissionCode.QrMenuView));

  // Первый доступный по правам подраздел редактора меню. Ссылка в навигации ведёт сразу на него
  // (абсолютным путём), чтобы не зависеть от relativeLinkResolution:'legacy' и не попадать на
  // «голый» /menu-edit (который из-за подмешанных роутов MenuModule может срезолвиться в меню).
  public menuEditFirstTab$ = combineLatest([
    this.hasGetPosProductView$,
    this.hasGetPosPromoView$,
    this.hasGetPosMenuView$,
  ]).pipe(
    map(([canProducts, canPromo, canMenu]) =>
      canProducts ? 'modifier-group' : canPromo ? 'promo' : canMenu ? 'rp-programs' : 'modifier-group'),
  );

  public isWidgetAvailable = false;
  public isQrMenuAvailable = false;
  public isContentManagerAvailable = false;
  public isKioskSettingsBridgeAvailable = false;
  public isControlCenterBridgeAvailable = false;
  public isDashboardBridgeAvailable = false;
  public isQrMenuBridgeAvailable = false;
  public isMenuSettingsBridgeAvailable = false;
  private currentToken: string | null = null;

  languages = [
    {
      id: 'en',
      title: 'English',
      flag: 'us'
    },
    {
      id: 'ru',
      title: 'Русский',
      flag: 'ru'
    }
  ];
  @Select(OrganizationsState.getSelectedOrganization) selectedOrganization$: Observable<Organization>;
  @Select(OrganizationsState.getSelectedOrganizationId) selectedOrganizationId$: Observable<string>;
  @ViewChild('profileTrigger') profileTrigger: MatMenuTrigger;
  hideKioskSettings = false;

  constructor(
    public router: Router,
    private store: Store,
    public translateService: TranslateService,
    public route: ActivatedRoute,
    private ngrxStore: ngrxStore,
  ) {
  }

  ngOnInit(): void {
    console.log(environment.publishedFor)
    // если нет ссылки на API виджета, то значит сервер его не поддерживает
    this.isWidgetAvailable = !!environment.widgetBaseUrl;
    this.isQrMenuAvailable = !!environment.qrMenuBaseUrl;
    this.isContentManagerAvailable = !!(environment as any).contentManagerPath;
    this.isKioskSettingsBridgeAvailable = !!(environment as any).kioskSettingsPath;
    this.isControlCenterBridgeAvailable = !!(environment as any).kioskControlCenterPath;
    this.isDashboardBridgeAvailable = !!(environment as any).dashboardPath;
    this.isQrMenuBridgeAvailable = !!(environment as any).qrMenuPath;
    this.isMenuSettingsBridgeAvailable = !!(environment as any).menuSettingsPath;

    this.ngrxStore.select(selectTokenInfo)
      .pipe(untilDestroyed(this))
      .subscribe(tokenInfo => {
        this.currentToken = tokenInfo?.token ?? null;
      });

    this.ngrxStore.select(selectLoginData).pipe(
      untilDestroyed(this)
    ).subscribe(user => {
      this.user = user;
    });

    this.selectedOrganizationId$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(organizationId => {
        this.selectedOrganizationId = organizationId;
      });

    this.ngrxStore.select(selectRole)
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(roleMembership => {
        this.roleMembership = roleMembership
      });
  }

  toggleMenu(): void {
    this.isOpen = !this.isOpen;
  }

  logout(): void {
    this.ngrxStore.dispatch(signOutUser());
    localStorage.removeItem('kioskToken');
  }

  setLanguage(lang): void {
    this.ngrxStore.dispatch(setLanguage(lang.id));
  }

  isMenuOpen =false;
  toggleMenuEdit(): void {this.isMenuOpen = !this.isMenuOpen;}

  // Подпункт «Права доступа» раскрывается кликом по «Пользователи» — так же, как редактор меню у «Меню».
  isUsersOpen = false;
  toggleUsersSection(): void {this.isUsersOpen = !this.isUsersOpen;}

  get canShowKioskSettings(): boolean {
    return (environment.publishedFor as PublishedFor) !== 'production';
  }

  get canShowQrMenuBridge(): boolean {
    return (environment.publishedFor as PublishedFor) !== 'production';
  }

  /**
   * Легаси-раздел QR-меню остаётся только на staging: на проде его заменил дочерний SPA
   * (пункт выше), и держать в меню два входа в одни настройки незачем.
   */
  get canShowLegacyQrMenu(): boolean {
    return (environment.publishedFor as PublishedFor) === 'staging';
  }

  /**
   * Новый микрофронт настроек меню пока обкатывается, поэтому вход в него открыт
   * только на staging: на остальных сборках menuSettingsPath пустой, но геттер
   * держит ограничение явным даже если путь туда случайно пропишут.
   */
  get canShowMenuSettingsBridge(): boolean {
    return (environment.publishedFor as PublishedFor) === 'staging';
  }

  get canShowContentManager(): boolean {
    return (environment.publishedFor as PublishedFor) !== 'production';
  }

  get canShowTrackingOrganizations(): boolean {
    return (environment.publishedFor as PublishedFor) !== 'kiosk';
  }

  goToContentManager(): void {
    const base = (environment as any).contentManagerPath as string;
    if (!base || !this.currentToken || !this.selectedOrganizationId) {
      return;
    }
    window.location.href = base;
  }

  goToKioskSettingsBridge(): void {
    const base = (environment as any).kioskSettingsPath as string;
    if (!base || !this.currentToken || !this.selectedOrganizationId) {
      return;
    }
    window.location.href = base;
  }

  goToControlCenterBridge(): void {
    const base = (environment as any).kioskControlCenterPath as string;
    if (!base || !this.currentToken || !this.selectedOrganizationId) {
      return;
    }
    window.location.href = base;
  }

  goToDashboardBridge(): void {
    const base = (environment as any).dashboardPath as string;
    if (!base || !this.currentToken || !this.selectedOrganizationId) {
      return;
    }
    window.location.href = base;
  }

  goToQrMenuBridge(): void {
    const base = (environment as any).qrMenuPath as string;
    if (!base || !this.currentToken || !this.selectedOrganizationId) {
      return;
    }
    window.location.href = base;
  }

  goToMenuSettingsBridge(): void {
    const base = (environment as any).menuSettingsPath as string;
    if (!base || !this.currentToken || !this.selectedOrganizationId) {
      return;
    }
    window.location.href = base;
  }
}
